import { useEffect, useState } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { isMobileDevice, isTelegramWebApp, isWalletBrowser, getWalletBrowserName } from '../utils/walletLinks';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

interface WalletConnectProps {
  onConnected?: (publicKey: string) => void;
}

export function WalletConnect({ onConnected }: WalletConnectProps) {
  const { webApp, isReady } = useTelegramWebApp();
  const { publicKey, connected, disconnect, connecting, select, wallet, walletAdapter } = useWallet();
  const { connection } = useConnection();
  const { setVisible } = useWalletModal();
  const isMobile = isMobileDevice();
  const [isInWalletBrowser, setIsInWalletBrowser] = useState(false);
  const [walletBrowserName, setWalletBrowserName] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const { visible } = useWalletModal();

  // Listen for wallet adapter errors (including WalletConnect errors)
  useEffect(() => {
    if (!walletAdapter) return;

    const handleError = (error: any) => {
      console.error('Wallet adapter error:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to connect wallet. Please try again.';
      
      if (error?.message) {
        const msg = error.message.toLowerCase();
        
        // WalletConnect specific errors
        if (msg.includes('user rejected') || msg.includes('user cancelled')) {
          errorMessage = 'Connection cancelled. Please try again when ready.';
        } else if (msg.includes('session rejected')) {
          errorMessage = 'Connection rejected. Please scan the QR code again.';
        } else if (msg.includes('timeout') || msg.includes('expired')) {
          errorMessage = 'Connection timed out. Please try again.';
        } else if (msg.includes('network') || msg.includes('connection')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setConnectionError(errorMessage);
    };

    walletAdapter.on?.('error', handleError);

    return () => {
      walletAdapter.off?.('error', handleError);
    };
  }, [walletAdapter]);

  // Detect if we're in a wallet browser
  useEffect(() => {
    const checkWalletBrowser = () => {
      const inWalletBrowser = isWalletBrowser();
      setIsInWalletBrowser(inWalletBrowser);
      setWalletBrowserName(getWalletBrowserName());
      
      // If we're in a wallet browser and have window.solana, try to auto-connect
      if (inWalletBrowser && (window as any).solana && !connected && !connecting) {
        // Check if we have a wallet adapter that matches
        const solana = (window as any).solana;
        console.log('Detected wallet browser, attempting auto-connect', {
          isPhantom: solana.isPhantom,
          isSolflare: solana.isSolflare,
          isTrust: solana.isTrust,
        });
        
        // Try to connect using the wallet adapter
        // The wallet adapter should auto-detect and connect
        if (select) {
          // Force select the appropriate wallet if available
          // This will be handled by the wallet adapter's autoConnect
        }
      }
    };
    
    checkWalletBrowser();
    // Check again after a short delay to catch late-loading wallets
    const timeout = setTimeout(checkWalletBrowser, 1000);
    
    return () => clearTimeout(timeout);
  }, [connected, connecting, select]);

  // Notify parent when wallet connects/disconnects
  useEffect(() => {
    if (connected && publicKey) {
      onConnected?.(publicKey.toString());
      setConnectionError(null); // Clear error on successful connection
    }
  }, [connected, publicKey, onConnected]);

  // Fetch SOL balance when wallet is connected
  useEffect(() => {
    if (!connected || !publicKey || !connection) {
      setSolBalance(null);
      return;
    }

    const fetchBalance = async () => {
      try {
        const balance = await connection.getBalance(publicKey);
        setSolBalance(balance / LAMPORTS_PER_SOL);
      } catch (error) {
        console.error('Error fetching SOL balance:', error);
        setSolBalance(null);
      }
    };

    fetchBalance();
    
    // Refresh balance every 10 seconds
    const interval = setInterval(fetchBalance, 10000);
    
    return () => clearInterval(interval);
  }, [connected, publicKey, connection]);

  // Clear error when connection state changes
  useEffect(() => {
    if (connecting) {
      setConnectionError(null);
    }
  }, [connecting]);

  // Block background interactions when modal is visible (PC only)
  useEffect(() => {
    // Only apply modal blocking for PC (where we use WalletModal)
    if (isMobile || isTelegramWebApp()) {
      return;
    }
    
    const appElement = document.querySelector('.app') as HTMLElement;
    let scrollY = 0;
    
    if (visible) {
      // Save scroll position
      scrollY = window.scrollY;
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      // Block pointer events on app content (not modal)
      if (appElement) {
        appElement.classList.add('modal-open');
      }
    } else {
      // Restore when modal is closed
      const savedScrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      
      // Restore scroll position
      if (savedScrollY) {
        const scrollValue = parseInt(savedScrollY.replace('px', '').replace('-', '') || '0');
        window.scrollTo(0, scrollValue);
      }
      
      if (appElement) {
        appElement.classList.remove('modal-open');
      }
    }
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      
      if (appElement) {
        appElement.classList.remove('modal-open');
      }
    };
  }, [visible, isMobile]);

  // Auto-open browser on desktop Telegram (only once, only in Telegram)
  useEffect(() => {
    // Check if we've already auto-opened in this session
    const alreadyOpened = sessionStorage.getItem('telegram_desktop_auto_opened');
    
    // Only auto-open if:
    // 1. We're actually in Telegram WebApp (has initData)
    // 2. It's desktop/non-mobile
    // 3. We haven't already opened this session
    if (
      isReady && 
      webApp && 
      !isMobile && 
      isTelegramWebApp() && 
      !alreadyOpened &&
      webApp.initData // Critical: only open if we have initData (actually in Telegram)
    ) {
      // Check if platform is desktop
      const platform = webApp.platform || '';
      if (platform === 'desktop' || (!isMobile && platform !== 'ios' && platform !== 'android')) {
        const frontendDomain = import.meta.env.VITE_FRONTEND_DOMAIN || 'https://www.dumbtrade.com';
        console.log('Auto-opening browser for desktop Telegram user:', frontendDomain);
        sessionStorage.setItem('telegram_desktop_auto_opened', 'true');
        webApp.openLink(frontendDomain);
      }
    }
  }, [isReady, webApp, isMobile]);

  // If wallet is connected, show disconnect button and balance
  if (connected && publicKey) {
    return (
      <div className="wallet-connected">
        <p>Connected: {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}</p>
        {solBalance !== null && (
          <p style={{ marginTop: '0.5rem', fontSize: '1.1rem', fontWeight: '600' }}>
            Balance: {solBalance.toFixed(4)} SOL
          </p>
        )}
        <button onClick={() => disconnect()} className="disconnect-button" style={{ marginTop: '1rem' }}>
          Disconnect
        </button>
      </div>
    );
  }

  // Desktop Telegram: Should auto-open browser, but show message if still here
  if (!isMobile && isTelegramWebApp()) {
    const frontendDomain = import.meta.env.VITE_FRONTEND_DOMAIN || 'https://www.dumbtrade.com';
    return (
      <div className="wallet-connect-desktop-telegram">
        <h3>Opening in Browser...</h3>
        <div className="telegram-desktop-info">
          <p>This app should open automatically in your browser.</p>
          <p>If it didn't open, click the button below:</p>
          <button 
            onClick={() => {
              if (webApp) {
                webApp.openLink(frontendDomain);
              } else {
                window.open(frontendDomain, '_blank');
              }
            }}
            className="open-browser-button"
          >
            Open in Browser
          </button>
          <p className="wallet-install-hint">
            Make sure you have a Solana wallet installed:{' '}
            <a href="https://phantom.app" target="_blank" rel="noopener noreferrer">Phantom</a> or{' '}
            <a href="https://solflare.com" target="_blank" rel="noopener noreferrer">Solflare</a>
          </p>
        </div>
      </div>
    );
  }

  // Mobile Telegram: Direct WalletConnect connection (no modal)
  if (isMobile && isTelegramWebApp()) {
    const handleMobileConnect = async () => {
      setConnectionError(null);
      
      try {
        // Select WalletConnect adapter directly
        // The select() function will automatically connect if the adapter supports it
        await select('WalletConnect');
        
        // Note: The wallet adapter handles connection internally after select()
        // For WalletConnect, it will show QR code or trigger deep links automatically
      } catch (error: any) {
        console.error('Error connecting WalletConnect:', error);
        setConnectionError(error?.message || 'Failed to connect wallet. Please try again.');
      }
    };
    
    return (
      <div className="wallet-connect-mobile">
        <button 
          onClick={handleMobileConnect} 
          className="connect-button"
          disabled={connecting}
        >
          {connecting ? 'Connecting...' : 'Connect Wallet'}
        </button>
        <p className="wallet-hint">
          Connect using WalletConnect (scan QR code or open wallet app)
        </p>
        {connectionError && (
          <p className="error-message" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
            {connectionError}
          </p>
        )}
      </div>
    );
  }

  // If we're in a wallet browser but not connected, try to connect directly
  if (isInWalletBrowser && !connected && !connecting) {
    const solana = (window as any).solana;
    
    return (
      <div className="wallet-connect">
        <div className="wallet-browser-detected">
          <p>Detected {walletBrowserName || 'wallet'} browser</p>
          {solana ? (
            <>
              <p className="wallet-hint">Click Connect Wallet to use your {walletBrowserName || 'wallet'}</p>
              <button 
                onClick={() => {
                  try {
                    // Open WalletConnect modal - it will detect the wallet browser and connect
                    setVisible(true);
                  } catch (error: any) {
                    console.error('Error opening wallet modal:', error);
                    setConnectionError('Failed to open wallet connection. Please try again.');
                    // If modal fails, try direct connection as fallback
                    if (solana && solana.connect) {
                      solana.connect().catch((connectError: any) => {
                        console.error('Error connecting wallet directly:', connectError);
                        setConnectionError(connectError.message || 'Failed to connect wallet.');
                      });
                    }
                  }
                }} 
                className="connect-button"
                disabled={connecting}
              >
                {connecting ? 'Connecting...' : 'Connect Wallet'}
              </button>
            </>
          ) : (
            <>
              <p className="wallet-hint">Wallet detected but provider not available. Please refresh the page.</p>
              <button 
                onClick={() => window.location.reload()} 
                className="connect-button"
              >
                Refresh Page
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Desktop browser or regular browser: Show connect button using WalletConnect modal
  // (PC only - mobile/Telegram uses direct connection above)
  const handleConnect = async () => {
    setConnectionError(null);
    try {
      // Open WalletModal - it handles both browser extensions and WalletConnect
      setVisible(true);
    } catch (error: any) {
      console.error('Error opening wallet modal:', error);
      setConnectionError('Failed to open wallet selection. Please try again.');
      
      // Fallback: try to connect directly if window.solana is available (for browser extensions)
      const solana = (window as any).solana;
      if (solana) {
        try {
          await solana.connect();
        } catch (connectError: any) {
          console.error('Error connecting wallet directly:', connectError);
          const errorMsg = connectError.message || 'Failed to connect wallet. Please try again.';
          setConnectionError(errorMsg);
        }
      } else {
        setConnectionError('No wallet detected. Please install Phantom, Solflare, or another Solana wallet extension.');
      }
    }
  };

  return (
    <div className="wallet-connect">
      <button 
        onClick={handleConnect} 
        className="connect-button"
        disabled={connecting}
      >
        {connecting ? 'Connecting...' : 'Connect Wallet'}
      </button>
      {connectionError && (
        <p className="error-message" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
          {connectionError}
        </p>
      )}
      {!isTelegramWebApp() && !connectionError && (
        <p className="wallet-hint">
          Connect your wallet using WalletConnect or browser extension
        </p>
      )}
    </div>
  );
}
